import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ApiConfig, UiSettingsControllerService } from '@osos/shared/api';
import { ManagedHttpContext } from '@osos/shared/types';
import { BehaviorSubject, forkJoin, from, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppConfiguration } from './app-configuration';
import { OrgConfiguration } from './org-configuration';
import {
  AvailableDateFormats,
  AvailableDateTimeFormats,
  AvailableTimeFormats,
  UserUiSettings,
} from './user-ui-settings';

const APP_CONFIGURATION_URL = 'app-config.json';
const ORG_CONFIGURATION_URL = 'org-config.json';
const THEME_STYLE_LINK = 'theme';
const USER_THEME_STYLE_LINK = 'user-theme';
const APP_FAV_ICON_LINK = 'appFavIcon';
const DEFAULT_FAV_ICON_LINK = '/assets/favicon/default-favicon.ico';
const VERSION_URL = 'version.json';

@Injectable()
export class ConfigService {
  private _configuration: AppConfiguration;
  private _orgConfig: OrgConfiguration;
  private _userUiConfig: UserUiSettings;
  private readonly renderer: Renderer2;

  private appConfigInitSub = new BehaviorSubject<AppConfiguration>(null);
  appConfigInit$ = this.appConfigInitSub.asObservable();

  private _appConfigSource = this.http
    .get<AppConfiguration>(APP_CONFIGURATION_URL)
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));
  private webVersion$ = this.http.get<any>(VERSION_URL).pipe(
    map((data) => data?.WEB_VERSION),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  get configuration(): AppConfiguration {
    return this._configuration ?? ({} as any);
  }

  get orgConfig(): OrgConfiguration {
    return this._orgConfig ?? ({} as any);
  }
  get userUiConfig(): UserUiSettings {
    return this._userUiConfig ?? ({} as UserUiSettings);
  }
  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfig,
    private settingService: UiSettingsControllerService,
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  loadAppConfiguration(): Observable<AppConfiguration> {
    return this._appConfigSource.pipe(
      map((configuration: AppConfiguration) => {
        // Set the AUTHORITY to the current origin if it's empty or null/undefined in the config:
        configuration.AUTHORITY ||= this.document.location.origin;

        const currentHost = window.location.host;
        const protocol = window.location.protocol;
        configuration.APP_URL = `${protocol}//${currentHost}`;

        // if (isDevMode()) {
        //   configuration.API_BASE_URL = `${protocol}//${currentHostName}:8000/api`;
        //   configuration.TIMELINE_API_BASE_URL = `${protocol}//${currentHostName}:8000/api/timeline`;
        //   configuration.NOTIFICATION_API_BASE_URL = `${protocol}//${currentHostName}:8000/api/notification`;
        //   configuration.COMMENTS_API_BASE_URL = `${protocol}//${currentHostName}:8000/api/comments`;
        //   configuration.STORAGE_BASE_URL = `${protocol}//${currentHostName}:8000/api`;
        //   configuration.ACCESS_BASE_URL = `${protocol}//${currentHostName}:8000/api`;
        //   configuration.WORKFLOW_API_BASE_URL = `${protocol}//${currentHostName}:8000/api/workflow`;
        //   configuration.REACTION_API_BASE_URL = `${protocol}//${currentHostName}:8000/api/reaction`;
        // }

        // Set API Config:
        this.apiConfig.set({
          dataApiBaseUrl: configuration.API_BASE_URL,
          timelineApiBaseUrl: configuration.TIMELINE_API_BASE_URL,
          workflowApiBaseUrl: configuration.WORKFLOW_API_BASE_URL,
          notificationApiBaseUrl: configuration.NOTIFICATION_API_BASE_URL,
          reactionApiBaseUrl: configuration.REACTION_API_BASE_URL,
          functionsApiBaseUrl: configuration.FUNCTIONS_API_BASE_URL,
          commentsApiBaseUrl: configuration.COMMENTS_API_BASE_URL,
          logsApiBaseUrl: configuration.LOGS_API_BASE_URL,
          qrCodeApiBaseUrl: configuration.QR_CODE_API_URL,
        });

        this.appConfigInitSub.next(configuration);

        this._configuration = configuration;
        return configuration;
      }),
    );
  }

  loadOrgConfiguration(): Observable<OrgConfiguration> {
    return this.http
      .get<OrgConfiguration>(ORG_CONFIGURATION_URL)
      .pipe(
        tap((orgConfig: OrgConfiguration) => (this._orgConfig = orgConfig)),
      );
  }
  loadVersion(): Observable<string> {
    return this.webVersion$;
  }

  loadUserSettings(settings) {
    this._userUiConfig = {
      dateFormat: settings?.dateFormat?._resolved?.mainTitle?.en
        ? AvailableDateFormats[settings.dateFormat._resolved.mainTitle.en]
        : 'yyyy-MM-dd',
      dateTimeFormat: settings?.dateTimeFormat?._resolved?.mainTitle?.en
        ? AvailableDateTimeFormats[
            settings.dateTimeFormat._resolved.mainTitle.en
          ]
        : 'mm/dd/yy hh:mm:ss a',
      default_theme: settings?.theme?.dataEntry,
      primaryLanguage: settings?.language?._resolved?.mainTitle,
      timeFormat: settings?.timeFormat?._resolved?.mainTitle?.en
        ? AvailableTimeFormats[settings.timeFormat._resolved.mainTitle.en]
        : 'hh:mm:ss a',
      timeZone: settings?.timeZone?._resolved?.mainTitle?.en,
    };
  }

  getApiBaseUrls(): string[] {
    return this.configuration
      ? [
          this.configuration.API_BASE_URL,
          this.configuration.TIMELINE_API_BASE_URL,
          this.configuration.WORKFLOW_API_BASE_URL,
          this.configuration.NOTIFICATION_API_BASE_URL,
          this.configuration.REACTION_API_BASE_URL,
          this.configuration.COMMENTS_API_BASE_URL,
          this.configuration.STORAGE_BASE_URL,
        ]
      : [];
  }

  initOrgPreferences(version): Observable<void> {
    return forkJoin([this._initTheme(version), this._initFavIcon()]).pipe(
      map(() => void 0),
    );
  }

  private _initTheme(version): Observable<void[]> {
    const head = this.document.getElementsByTagName('head')[0];
    const styleFilesLoaded: Array<Promise<void>> = [];
    // Load org theme
    const themeLink = this.renderer.createElement('link') as HTMLLinkElement;
    let headlessStyle: HTMLLinkElement | null;
    themeLink.id = THEME_STYLE_LINK;
    themeLink.rel = 'stylesheet';
    themeLink.href = `${this.orgConfig.THEME ?? 'default'}-theme.css?v=${version}`;

    if (this._configuration?.IS_HEADLESS_BROWSER) {
      headlessStyle = this.renderer.createElement('link') as HTMLLinkElement;
      headlessStyle.rel = 'stylesheet';
      headlessStyle.href = 'roaa-headless-style.css';
    }
    const themeLoaded = new Promise<void>((resolve) => {
      themeLink.onload = () => resolve();
    });
    styleFilesLoaded.push(themeLoaded);
    if (headlessStyle) {
      const headlessThemeLoaded = new Promise<void>((resolve) => {
        headlessStyle.onload = () => resolve();
      });
      styleFilesLoaded.push(headlessThemeLoaded);
    }
    this.renderer.appendChild(head, themeLink);
    headlessStyle && this.renderer.appendChild(head, headlessStyle);
    if (this._userUiConfig?.default_theme) {
      this.settingService
        .getThemeCssFile({
          context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
          themeId: this._userUiConfig.default_theme,
        })
        .pipe(
          switchMap((themeCssFile) => {
            const head = this.document.getElementsByTagName('head')[0];

            // Create a Blob from the CSS content
            const cssBlob = new Blob([themeCssFile], { type: 'text/css' });

            // Create a URL for the Blob
            const cssBlobUrl = URL.createObjectURL(cssBlob);

            // Create a <link> element
            const customThemeLink = this.renderer.createElement('link');
            customThemeLink.rel = 'stylesheet';
            customThemeLink.href = cssBlobUrl;
            customThemeLink.id = USER_THEME_STYLE_LINK;
            const customThemeLoaded = new Promise<void>((resolve) => {
              customThemeLink.onload = () => resolve();
            });
            styleFilesLoaded.push(customThemeLoaded);

            if (headlessStyle) {
              const headlessThemeLoaded = new Promise<void>((resolve) => {
                headlessStyle.onload = () => resolve();
              });
              styleFilesLoaded.push(headlessThemeLoaded);
            }

            this.renderer.appendChild(head, customThemeLink);
            headlessStyle && this.renderer.appendChild(head, headlessStyle);

            return forkJoin(
              styleFilesLoaded.map((fileLoaded) => from(fileLoaded)),
            );
          }),
          map(() => void 0),
        )
        .subscribe();
    }
    return forkJoin(styleFilesLoaded.map((fileLoaded) => from(fileLoaded)));
  }

  private _initFavIcon(): Observable<void> {
    const head = this.document.getElementsByTagName('head')[0];
    const faviconLink = this.renderer.createElement('link') as HTMLLinkElement;
    faviconLink.id = APP_FAV_ICON_LINK;
    faviconLink.rel = 'icon';
    faviconLink.href = `${
      this.orgConfig.FAV_ICON_URL ?? DEFAULT_FAV_ICON_LINK
    }`;

    const faviconLoaded = new Promise<void>((resolve) => {
      faviconLink.onload = () => resolve();
    });

    this.renderer.appendChild(head, faviconLink);

    return from(faviconLoaded);
  }
}
